import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Youtube, Linkedin, Send } from "lucide-react";
import { BRAND, POLICIES } from "../../data/content";
import { LOCATION_ITEMS, PRODUCT_GROUPS, BLOG_CATEGORIES } from "../../data/seoContent";
import { useState } from "react";
import { subscribeNewsletter } from "../../lib/api";
import { toast } from "sonner";
import { useCms } from "../../cms/CmsContext";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: cmsData } = useCms();
  const contact = cmsData?.contact || {};
  const footerCms = cmsData?.footer || {};
  const phone = contact.phone || BRAND.phone;
  const phoneRaw = phone.replace(/[^0-9]/g, "");
  const email2 = contact.email || BRAND.email;
  const address = contact.address || BRAND.address;
  const social = { ...BRAND.social, ...(contact.social || {}) };
  const tagline = footerCms.tagline ||
    "Intelligence at Your Command. Ultra-premium smart home automation for India's most discerning homes, villas, hotels and offices.";
  const copyrightText = (footerCms.copyright_text || "© {year} GenieX. All rights reserved. Intelligence at Your Command.™").replace(
    "{year}",
    new Date().getFullYear()
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      setBusy(true);
      await subscribeNewsletter(email);
      toast.success("Welcome to GenieX. We'll be in touch.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setBusy(false);
    }
  };

  // Top product items for footer (use SEO content as single source of truth)
  const topProducts = PRODUCT_GROUPS.flatMap((g) => g.items).slice(0, 10);

  return (
    <footer data-testid="site-footer" className="relative border-t border-line/70 bg-[var(--gx-black)]">
      <div className="absolute top-0 left-0 right-0 gold-line opacity-50" />

      {/* CTA strip */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 lg:py-24 border-b border-line/60">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-20 items-end">
          <div>
            <p className="eyebrow mb-4">A Quiet Revolution</p>
            <h2 className="h2-display text-white max-w-[18ch]">
              Begin your intelligent living journey.
            </h2>
            <p className="lead mt-6 max-w-[52ch]">
              Receive the GenieX Lookbook — a private guide to the world's most discreet smart homes,
              designed for India.
            </p>
          </div>
          <form onSubmit={submit} className="flex flex-col gap-3 w-full" data-testid="footer-newsletter">
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                data-testid="newsletter-email"
                className="flex-1 bg-transparent border border-line focus:border-gold/60 outline-none rounded-full px-5 py-3.5 text-[14px] text-white placeholder:text-mute transition-colors"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="newsletter-submit"
                className="btn-gold whitespace-nowrap disabled:opacity-60"
              >
                {busy ? "Subscribing…" : (<><Send className="w-3.5 h-3.5" /> Subscribe</>)}
              </button>
            </div>
            <p className="text-[11px] text-mute pl-1">
              No spam. Just inspiration, twice a month.
            </p>
          </form>
        </div>
      </div>

      {/* Main footer grid */}
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14 lg:py-20 grid grid-cols-2 lg:grid-cols-6 gap-10 lg:gap-14">
        <div className="col-span-2 lg:col-span-2">
          <Link to="/" className="font-display text-2xl tracking-tight">
            Genie<span className="text-gold">X</span>
          </Link>
          <p className="text-dim text-[14px] leading-relaxed max-w-[36ch] mt-5">
            {tagline}
          </p>
          <div className="mt-7 space-y-3 text-[13px]">
            <a href={`tel:+${phoneRaw}`} className="flex items-center gap-3 text-dim hover:text-gold transition-colors" data-testid="footer-phone">
              <Phone className="w-3.5 h-3.5 text-gold" /> {phone}
            </a>
            <a href={`mailto:${email2}`} className="flex items-center gap-3 text-dim hover:text-gold transition-colors" data-testid="footer-email">
              <Mail className="w-3.5 h-3.5 text-gold" /> {email2}
            </a>
            <p className="flex items-center gap-3 text-dim">
              <MapPin className="w-3.5 h-3.5 text-gold" /> {address}
            </p>
          </div>
          <div className="flex gap-3 mt-7">
            {[
              { Icon: Instagram, href: social.instagram, key: "ig" },
              { Icon: Facebook, href: social.facebook, key: "fb" },
              { Icon: Youtube, href: social.youtube, key: "yt" },
              { Icon: Linkedin, href: social.linkedin, key: "li" },
            ].map(({ Icon, href, key }) => (
              <a
                key={key}
                href={href}
                aria-label={key}
                className="w-9 h-9 rounded-full border border-line hover:border-gold hover:text-gold text-dim flex items-center justify-center transition-colors"
              >
                <Icon className="w-3.5 h-3.5" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <p className="eyebrow text-mute mb-4">Explore</p>
          <ul className="space-y-2.5 text-[13px]">
            <li><Link to="/dynamic-series" className="text-dim hover:text-gold transition-colors">Dynamic Series</Link></li>
            <li><Link to="/experience" className="text-gold hover:underline transition-colors" data-testid="footer-experience-link">Experience Center →</Link></li>
            <li><Link to="/solutions/complete-home" className="text-dim hover:text-gold transition-colors">Complete Home</Link></li>
            <li><Link to="/solutions/home-theatre" className="text-dim hover:text-gold transition-colors">Home Theatre</Link></li>
            <li><Link to="/solutions/home-security" className="text-dim hover:text-gold transition-colors">Home Security</Link></li>
            <li><Link to="/solutions/for-architects" className="text-dim hover:text-gold transition-colors">For Architects</Link></li>
            <li><Link to="/about" className="text-dim hover:text-gold transition-colors">About</Link></li>
            <li><Link to="/blog" className="text-dim hover:text-gold transition-colors">Journal</Link></li>
            <li><Link to="/contact" className="text-dim hover:text-gold transition-colors">Contact</Link></li>
          </ul>
        </div>

        <div>
          <p className="eyebrow text-mute mb-4">Products</p>
          <ul className="space-y-2.5 text-[13px]">
            {topProducts.map((c) => (
              <li key={c.slug}>
                <Link to={`/products/${c.slug}`} className="text-dim hover:text-gold transition-colors">
                  {c.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="eyebrow text-mute mb-4">Service Areas</p>
          <ul className="space-y-2.5 text-[13px]">
            {LOCATION_ITEMS.map((l) => (
              <li key={l.slug}>
                <Link to={`/smart-home/${l.slug}`} className="text-dim hover:text-gold transition-colors">
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="col-span-2 lg:col-span-1">
          <p className="eyebrow text-mute mb-4">Journal</p>
          <ul className="space-y-2.5 text-[13px]">
            {BLOG_CATEGORIES.slice(0, 6).map((b) => (
              <li key={b.slug}>
                <Link to={`/blog`} className="text-dim hover:text-gold transition-colors">
                  {b.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/blog" className="text-gold hover:underline">All articles →</Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-line/60">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-6 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 text-[11.5px] text-mute">
          <p>{copyrightText}</p>
          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            {POLICIES.map((p) => (
              <li key={p.slug}>
                <Link to={`/${p.slug}`} className="hover:text-gold transition-colors">
                  {p.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
